import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { PlatformService } from './platform.service';
import { GlobalEventsService } from './global-events.service';

export interface HistoryRoute {
  /**
   * Route url
   */
  url: string;

  /**
   * Scroll top position for current route
   */
  scrollTop: number;

  /**
   * Additional data for route
   */
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LocalHistoryService {
  private _router = inject(Router);
  private _platform = inject(PlatformService);
  private _globalEvent = inject(GlobalEventsService);


  /**
   * Routes in history
   */
  private _routes: Map<string, HistoryRoute> = new Map();

  /**
   * Navigation history
   */
  private _history: Array<string> = [];

  constructor() {
    this._handleRouterEvents();
  }

  /**
   * Access to history
   */
  get history(): Array<string> {
    return this._history;
  }

  /**
   * Access to routes
   */
  get routes() {
    return this._routes;
  }

  /**
   * Add URL to history
   *
   * @param key
   * @param url
   * @param data
   */
  addUrl(key: string, url: string, data?: any) {
    this._routes.set(key, {
      url,
      scrollTop: this._platform.isBrowser ? window.scrollY : 0,
      data
    });
  }

  /**
   * Get URL from history by URL
   *
   * @param key
   */
  getUrl(key: string): HistoryRoute | any {
    if (this._routes.has(key)) {
      return this._routes.get(key);
    }

    return null;
  }

  /**
   * Remove URL from history by key
   *
   * @param key
   */
  removeUrl(key: string): any {
    if (this._routes.has(key)) {
      return this._routes.delete(key);
    }
  }

  /**
   * Redirect to provided url not adding current url to browser history
   *
   * @param url
   */
  skipHistoryNavigate(url: string) {
    this._router.navigateByUrl(url, {
      replaceUrl: true
    }).then();
  }

  /**
   *
   * @param key
   * @param removeFromHistory
   */
  restoreScrollByKey(key: string, removeFromHistory: boolean = true) {
    if (this._routes.has(key) && this._platform.isBrowser) {
      const url = this.getUrl(key);
      if (url.url === this._router.url) {
        window.scroll({
          top: url.scrollTop,
          left: 0,
          behavior: 'smooth'
        });
      }
    }

    if (removeFromHistory) {
      this.removeUrl(key);
    }
  }

  getPreviousUrl() {
    if (this.history.length > 1) {
      return this.history[this.history.length - 2];
    } else {
      return '/';
    }
  }

  /**
   * Handle router events for saving history
   *
   * @private
   */
  private _handleRouterEvents() {
    this._globalEvent.routerNavigationEnd$.pipe(
      tap(({urlAfterRedirects}: NavigationEnd) => {
        const urlAfterToCompare = urlAfterRedirects.split('/')[urlAfterRedirects?.split('/').length - 1];
        const lenHistory = this._history.length - 1;
        const lenHistoryUrl = this._history[lenHistory]?.split('/').length - 1;
        if (urlAfterToCompare !== this._history[lenHistory]?.split('/')[lenHistoryUrl]){
          this._history.push(urlAfterRedirects);
        }
      })
    ).subscribe();
  }
}
